import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Megaphone, Plus, Heart, Send, Award, MessageCircle, Users, Cake,
  Pin, ThumbsUp, PartyPopper, Flame, Star, Smile, MessageSquare,
  Paperclip, Download, Search, Trash2, MoreHorizontal, Calendar,
  Trophy, Lightbulb, Shield, Zap, UserPlus, ArrowRight, Hash,
  FileText, Image, X
} from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";

const ax = () => { const t = localStorage.getItem("token"); return { headers: t ? { Authorization: `Bearer ${t}` } : {} }; };

const REACTIONS = [
  { key: "like", icon: ThumbsUp, label: "Me gusta", emoji: "👍" },
  { key: "clap", icon: PartyPopper, label: "Aplaudir", emoji: "👏" },
  { key: "celebrate", icon: Flame, label: "Celebrar", emoji: "🎉" },
  { key: "support", icon: Heart, label: "Apoyar", emoji: "❤️" },
  { key: "wow", icon: Star, label: "Asombrar", emoji: "⭐" },
];

const REC_CATEGORIES = [
  { value: "teamwork", label: "Trabajo en Equipo", icon: Users, color: "bg-blue-100 text-blue-700 border-blue-300" },
  { value: "innovation", label: "Innovación", icon: Lightbulb, color: "bg-violet-100 text-violet-700 border-violet-300" },
  { value: "leadership", label: "Liderazgo", icon: Shield, color: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  { value: "dedication", label: "Dedicación", icon: Zap, color: "bg-amber-100 text-amber-700 border-amber-300" },
  { value: "customer", label: "Servicio al Cliente", icon: Smile, color: "bg-pink-100 text-pink-700 border-pink-300" },
  { value: "excellence", label: "Excelencia", icon: Trophy, color: "bg-cyan-100 text-cyan-700 border-cyan-300" },
];

const getCatInfo = (cat) => REC_CATEGORIES.find(c => c.value === cat) || REC_CATEGORIES[0];
const countReactions = (reactions) => {
  if (!reactions || typeof reactions !== "object") return 0;
  return Object.values(reactions).reduce((s, arr) => s + (Array.isArray(arr) ? arr.length : 0), 0);
};

// === REACTION BAR ===
const ReactionBar = ({ reactions, onReact, userId }) => {
  const [showPicker, setShowPicker] = useState(false);
  const userReaction = useMemo(() => {
    if (!reactions || typeof reactions !== "object") return null;
    for (const [key, users] of Object.entries(reactions)) {
      if (Array.isArray(users) && users.includes(userId)) return key;
    }
    return null;
  }, [reactions, userId]);

  return (
    <div className="flex items-center gap-1 relative">
      <button onClick={() => setShowPicker(!showPicker)} className={`px-2 py-1 rounded-full text-xs border transition-all hover:bg-slate-100 ${userReaction ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "border-slate-200 text-slate-500"}`}
        data-testid="reaction-toggle">
        {userReaction ? REACTIONS.find(r => r.key === userReaction)?.emoji : "+"} {countReactions(reactions) > 0 ? countReactions(reactions) : "Reaccionar"}
      </button>
      {showPicker && (
        <div className="absolute bottom-full left-0 mb-1 flex gap-0.5 bg-white rounded-full shadow-lg border border-slate-200 px-1.5 py-1 z-20">
          {REACTIONS.map(r => (
            <button key={r.key} onClick={() => { onReact(r.key); setShowPicker(false); }}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-base hover:scale-125 transition-transform ${userReaction === r.key ? "bg-emerald-100" : "hover:bg-slate-100"}`}
              title={r.label} data-testid={`react-${r.key}`}>{r.emoji}</button>
          ))}
        </div>
      )}
      {/* Show active reaction counts */}
      {reactions && typeof reactions === "object" && Object.entries(reactions).filter(([, u]) => Array.isArray(u) && u.length > 0).map(([key, users]) => (
        <span key={key} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-slate-100 text-xs text-slate-600">
          {REACTIONS.find(r => r.key === key)?.emoji} {users.length}
        </span>
      ))}
    </div>
  );
};

const Communications = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [recognitions, setRecognitions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [stats, setStats] = useState(null);
  const [chatGroups, setChatGroups] = useState([]);
  const [chatUsers, setChatUsers] = useState([]);
  const [currentGroup, setCurrentGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [rules, setRules] = useState([]);
  const [notificationLogs, setNotificationLogs] = useState([]);
  const [gupshupConfig, setGupshupConfig] = useState({ waba_name: "", app_name: "", phone_number: "", sandbox: true, credentials: { api_key: "", app_token: "" } });
  const [broadcastForm, setBroadcastForm] = useState({ title: "", message: "", channels: ["internal_chat"], audience: { work_center_id: "", department: "" } });
  const [ruleForm, setRuleForm] = useState({ name: "", event_type: "leave_request_created", channels: ["internal_chat"], audience: { work_center_id: "", department: "" }, message_template: "" });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("muro");
  const [searchTerm, setSearchTerm] = useState("");

  // Dialogs
  const [showPostDlg, setShowPostDlg] = useState(false);
  const [showRecDlg, setShowRecDlg] = useState(false);
  const [showGroupDlg, setShowGroupDlg] = useState(false);
  const [commentingOn, setCommentingOn] = useState(null);
  const [commentText, setCommentText] = useState("");

  // Forms
  const [newPost, setNewPost] = useState({ title: "", content: "", priority: "normal", pinned: false });
  const [newRec, setNewRec] = useState({ to_employee_id: "", message: "", category: "teamwork" });
  const [newGroup, setNewGroup] = useState({ name: "", description: "", members: [] });
  const [chatInput, setChatInput] = useState("");
  const [chatFile, setChatFile] = useState(null);
  const msgEndRef = useRef(null);
  const pollRef = useRef(null);

  const isAdmin = ["super_admin", "admin_empresa", "gerente_rh", "supervisor"].includes(user?.role);

  const fetchData = useCallback(async () => {
    try {
      const [postsR, recsR, empsR, bdR, statsR, grpsR, usrsR, rulesR, logsR, gupshupR] = await Promise.all([
        axios.get(`${API}/announcements`, ax()).catch(() => ({ data: [] })),
        axios.get(`${API}/recognitions`, ax()).catch(() => ({ data: [] })),
        axios.get(`${API}/employees`, ax()).catch(() => ({ data: [] })),
        axios.get(`${API}/portal/birthdays`, ax()).catch(() => ({ data: [] })),
        axios.get(`${API}/comms/stats`, ax()).catch(() => ({ data: null })),
        axios.get(`${API}/chat/groups`, ax()).catch(() => ({ data: [] })),
        axios.get(`${API}/chat/users`, ax()).catch(() => ({ data: [] })),
        axios.get(`${API}/notification-rules`, ax()).catch(() => ({ data: [] })),
        axios.get(`${API}/notification-logs`, ax()).catch(() => ({ data: [] })),
        axios.get(`${API}/company/integrations/gupshup`, ax()).catch(() => ({ data: {} })),
      ]);
      setPosts(postsR.data || []);
      setRecognitions(recsR.data || []);
      setEmployees(empsR.data || []);
      setBirthdays(bdR.data || []);
      setStats(statsR.data);
      setChatGroups(grpsR.data || []);
      setChatUsers(usrsR.data || []);
      setRules(rulesR.data || []);
      setNotificationLogs(logsR.data || []);
      if (gupshupR.data) setGupshupConfig(previous => ({ ...previous, ...gupshupR.data }));
    } catch { toast.error("Error al cargar datos"); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Chat polling
  useEffect(() => {
    if (currentGroup && tab === "chat") {
      const loadMsgs = async () => {
        try {
          const res = await axios.get(`${API}/chat/groups/${currentGroup.group_id}/messages`, ax());
          setMessages(res.data || []);
        } catch {}
      };
      loadMsgs();
      pollRef.current = setInterval(loadMsgs, 4000);
      return () => clearInterval(pollRef.current);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [currentGroup, tab]);

  useEffect(() => { msgEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const getEmpName = (id) => { const e = employees.find(x => x.employee_id === id); return e ? `${e.first_name} ${e.last_name}` : id || ""; };

  // === HANDLERS ===
  const handleCreatePost = async () => {
    if (!newPost.title) { toast.error("Título requerido"); return; }
    try {
      await axios.post(`${API}/announcements`, newPost, ax());
      toast.success("Publicación creada"); setShowPostDlg(false);
      setNewPost({ title: "", content: "", priority: "normal", pinned: false }); fetchData();
    } catch { toast.error("Error"); }
  };

  const handleCreateRec = async () => {
    if (!newRec.to_employee_id || !newRec.message) { toast.error("Empleado y mensaje requeridos"); return; }
    try {
      await axios.post(`${API}/recognitions`, newRec, ax());
      toast.success("Reconocimiento enviado"); setShowRecDlg(false);
      setNewRec({ to_employee_id: "", message: "", category: "teamwork" }); fetchData();
    } catch { toast.error("Error"); }
  };

  const handleReactPost = async (id, reaction) => {
    try { const res = await axios.post(`${API}/announcements/${id}/react`, { reaction }, ax()); setPosts(prev => prev.map(p => p.announcement_id === id ? { ...p, reactions: res.data.reactions } : p)); }
    catch { toast.error("Error"); }
  };

  const handleReactRec = async (id, reaction) => {
    try { const res = await axios.post(`${API}/recognitions/${id}/react`, { reaction }, ax()); setRecognitions(prev => prev.map(r => r.recognition_id === id ? { ...r, reactions: res.data.reactions } : r)); }
    catch { toast.error("Error"); }
  };

  const handleCommentPost = async (id) => {
    if (!commentText.trim()) return;
    try {
      const res = await axios.post(`${API}/announcements/${id}/comment`, { text: commentText }, ax());
      setPosts(prev => prev.map(p => p.announcement_id === id ? { ...p, comments: [...(p.comments || []), res.data] } : p));
      setCommentText(""); setCommentingOn(null);
    } catch { toast.error("Error"); }
  };

  const handleCommentRec = async (id) => {
    if (!commentText.trim()) return;
    try {
      const res = await axios.post(`${API}/recognitions/${id}/comment`, { text: commentText }, ax());
      setRecognitions(prev => prev.map(r => r.recognition_id === id ? { ...r, comments: [...(r.comments || []), res.data] } : r));
      setCommentText(""); setCommentingOn(null);
    } catch { toast.error("Error"); }
  };

  const handlePinPost = async (id) => {
    try { const res = await axios.put(`${API}/announcements/${id}/pin`, {}, ax()); setPosts(prev => prev.map(p => p.announcement_id === id ? { ...p, pinned: res.data.pinned } : p)); }
    catch { toast.error("Error"); }
  };

  const handleDeletePost = async (id) => {
    if (!window.confirm("Eliminar esta publicación?")) return;
    try { await axios.delete(`${API}/announcements/${id}`, ax()); setPosts(prev => prev.filter(p => p.announcement_id !== id)); toast.success("Eliminada"); }
    catch { toast.error("Error"); }
  };

  const handleCreateGroup = async () => {
    if (!newGroup.name) { toast.error("Nombre requerido"); return; }
    try {
      await axios.post(`${API}/chat/groups`, newGroup, ax());
      toast.success("Grupo creado"); setShowGroupDlg(false);
      setNewGroup({ name: "", description: "", members: [] }); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleSendMessage = async () => {
    if (!chatInput.trim() && !chatFile) return;
    if (!currentGroup) return;
    try {
      let payload = { text: chatInput };
      if (chatFile) {
        const reader = new FileReader();
        const fileData = await new Promise((resolve) => {
          reader.onload = (e) => resolve(e.target.result.split(",")[1]);
          reader.readAsDataURL(chatFile);
        });
        payload.file_name = chatFile.name;
        payload.file_data = fileData;
        payload.file_type = chatFile.type;
      }
      await axios.post(`${API}/chat/groups/${currentGroup.group_id}/messages`, payload, ax());
      setChatInput(""); setChatFile(null);
      const res = await axios.get(`${API}/chat/groups/${currentGroup.group_id}/messages`, ax());
      setMessages(res.data || []);
    } catch { toast.error("Error al enviar"); }
  };

  const handleDownloadFile = async (msgId, fileName) => {
    try {
      const res = await axios.get(`${API}/chat/messages/${msgId}/file`, { ...ax(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = fileName; a.click();
      toast.success("Archivo descargado (eliminado del servidor)");
    } catch { toast.error("Archivo no disponible"); }
  };

  const handleExportRecs = async () => {
    try {
      const res = await axios.get(`${API}/recognitions/export/csv`, { ...ax(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "reconocimientos.csv"; a.click();
    } catch { toast.error("Error al exportar"); }
  };

  const filteredPosts = posts.filter(p => !searchTerm || p.title?.toLowerCase().includes(searchTerm.toLowerCase()) || p.content?.toLowerCase().includes(searchTerm.toLowerCase()));
  const sortedPosts = [...filteredPosts].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
  const workCenters = [...new Set(employees.map(employee => employee.work_center_id ? JSON.stringify({ id: employee.work_center_id, name: employee.work_center_name }) : null).filter(Boolean))].map(item => JSON.parse(item));

  const handleSaveRule = async () => {
    try {
      await axios.post(`${API}/notification-rules`, ruleForm, ax());
      toast.success("Regla guardada");
      setRuleForm({ name: "", event_type: "leave_request_created", channels: ["internal_chat"], audience: { work_center_id: "", department: "" }, message_template: "" });
      fetchData();
    } catch { toast.error("No se pudo guardar la regla"); }
  };

  const handleSaveGupshup = async () => {
    try {
      await axios.post(`${API}/company/integrations/gupshup`, gupshupConfig, ax());
      toast.success("Configuración Gupshup guardada");
      fetchData();
    } catch { toast.error("No se pudo guardar Gupshup"); }
  };

  const handleTestGupshup = async () => {
    try {
      const response = await axios.post(`${API}/company/integrations/gupshup/test`, {}, ax());
      toast.success(response.data.message);
    } catch { toast.error("No se pudo probar Gupshup"); }
  };

  const handleBroadcast = async () => {
    try {
      const response = await axios.post(`${API}/notifications/broadcast`, broadcastForm, ax());
      toast.success(`Difusión registrada para ${response.data.recipients} destinatario(s)`);
      fetchData();
    } catch { toast.error("No se pudo registrar la difusión"); }
  };

  if (loading) return <Layout title="Comunicación"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div></Layout>;

  return (
    <Layout title="Comunicación y Reconocimiento" subtitle="Muro social, reconocimientos, chat grupal y cumpleaños"
      actions={<div className="flex gap-2 flex-wrap">
        {isAdmin && <Button onClick={() => setShowPostDlg(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-post-btn"><Plus className="w-4 h-4 mr-2" />Publicar</Button>}
        <Button variant="outline" onClick={() => setShowRecDlg(true)} data-testid="create-rec-btn"><Award className="w-4 h-4 mr-2" />Reconocer</Button>
      </div>}>

      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="comms-kpis">
          {[
            { label: "Publicaciones", value: stats.total_posts, icon: Megaphone, color: "bg-blue-100 text-blue-600" },
            { label: "Reconocimientos (Mes)", value: stats.month_recognitions, icon: Award, color: "bg-amber-100 text-amber-600" },
            { label: "Reconocimientos Total", value: stats.total_recognitions, icon: Trophy, color: "bg-emerald-100 text-emerald-600" },
            { label: "Chats Activos", value: stats.active_chats, icon: MessageSquare, color: "bg-violet-100 text-violet-600" },
            { label: "Cumpleañeros", value: birthdays.length, icon: Cake, color: "bg-pink-100 text-pink-600" },
          ].map((s, i) => (
            <Card key={i} className="border-slate-200"><CardContent className="p-3.5"><div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${s.color}`}><s.icon className="w-4 h-4" /></div>
              <div><p className="text-xl font-bold text-slate-900">{s.value}</p><p className="text-[11px] text-slate-500">{s.label}</p></div>
            </div></CardContent></Card>
          ))}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 flex-wrap h-auto gap-1" data-testid="comms-tabs">
          <TabsTrigger value="muro" data-testid="tab-muro"><Megaphone className="w-3.5 h-3.5 mr-1" />Muro</TabsTrigger>
          <TabsTrigger value="recognitions" data-testid="tab-recs"><Award className="w-3.5 h-3.5 mr-1" />Reconocimientos</TabsTrigger>
          <TabsTrigger value="chat" data-testid="tab-chat"><MessageSquare className="w-3.5 h-3.5 mr-1" />Chat</TabsTrigger>
          <TabsTrigger value="birthdays" data-testid="tab-birthdays"><Cake className="w-3.5 h-3.5 mr-1" />Cumpleaños</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications"><Send className="w-3.5 h-3.5 mr-1" />Notificaciones</TabsTrigger>
        </TabsList>

        {/* ===== MURO SOCIAL ===== */}
        <TabsContent value="muro">
          <div className="mb-4">
            <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Buscar en el muro..." className="pl-10" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="search-muro" />
            </div>
          </div>
          {sortedPosts.length === 0 ? (
            <Card><CardContent className="p-10 text-center text-slate-500" data-testid="muro-empty"><Megaphone className="w-14 h-14 mx-auto mb-3 text-slate-300" /><p className="font-medium text-lg">Muro vacío</p><p className="text-sm mt-1">Crea la primera publicación para tu equipo</p></CardContent></Card>
          ) : (
            <div className="space-y-4 max-w-3xl mx-auto">
              {sortedPosts.map(post => (
                <Card key={post.announcement_id} className={`border-slate-200 hover:shadow-md transition-all ${post.pinned ? "border-l-4 border-l-amber-400" : ""}`} data-testid={`post-${post.announcement_id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-sm font-bold">
                          {post.author_name?.split(" ").map(n => n[0]).join("").slice(0, 2) || "A"}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900 text-sm">{post.author_name || "Admin"}</p>
                          <p className="text-[11px] text-slate-400">{post.published_at ? new Date(post.published_at).toLocaleDateString("es-MX", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {post.pinned && <Badge className="bg-amber-100 text-amber-700 text-[10px]"><Pin className="w-3 h-3 mr-0.5" />Fijado</Badge>}
                        {post.priority === "high" && <Badge className="bg-red-100 text-red-700 text-[10px]">Importante</Badge>}
                        {isAdmin && (
                          <div className="flex gap-0.5 ml-2">
                            <button onClick={() => handlePinPost(post.announcement_id)} className="p-1.5 rounded-lg hover:bg-slate-100" title="Fijar" data-testid={`pin-${post.announcement_id}`}><Pin className="w-3.5 h-3.5 text-slate-400" /></button>
                            <button onClick={() => handleDeletePost(post.announcement_id)} className="p-1.5 rounded-lg hover:bg-red-50" title="Eliminar" data-testid={`del-${post.announcement_id}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                          </div>
                        )}
                      </div>
                    </div>
                    <h3 className="font-bold text-slate-900 text-base mb-1">{post.title}</h3>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">{post.content}</p>
                    {/* Reactions */}
                    <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
                      <ReactionBar reactions={post.reactions} onReact={(r) => handleReactPost(post.announcement_id, r)} userId={user?.user_id} />
                      <button onClick={() => { setCommentingOn(commentingOn === `post-${post.announcement_id}` ? null : `post-${post.announcement_id}`); setCommentText(""); }}
                        className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 px-2 py-1 rounded-lg hover:bg-slate-100" data-testid={`comment-toggle-${post.announcement_id}`}>
                        <MessageCircle className="w-3.5 h-3.5" />{(post.comments || []).length} comentarios
                      </button>
                    </div>
                    {/* Comments */}
                    {(post.comments || []).length > 0 && (
                      <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
                        {post.comments.slice(-3).map(c => (
                          <div key={c.comment_id} className="flex items-start gap-2">
                            <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[9px] font-bold text-slate-600 shrink-0 mt-0.5">
                              {c.author_name?.charAt(0) || "?"}
                            </div>
                            <div className="bg-slate-50 rounded-lg px-3 py-1.5 flex-1">
                              <p className="text-xs font-semibold text-slate-700">{c.author_name}</p>
                              <p className="text-xs text-slate-600">{c.text}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {commentingOn === `post-${post.announcement_id}` && (
                      <div className="mt-2 flex gap-2">
                        <Input value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Escribe un comentario..."
                          className="text-sm h-9" onKeyDown={e => e.key === "Enter" && handleCommentPost(post.announcement_id)} data-testid={`comment-input-${post.announcement_id}`} />
                        <Button size="sm" onClick={() => handleCommentPost(post.announcement_id)} className="h-9 bg-emerald-600 hover:bg-emerald-700"><Send className="w-3.5 h-3.5" /></Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ===== RECOGNITIONS ===== */}
        <TabsContent value="recognitions">
          <div className="flex justify-between items-center mb-4">
            <div className="flex gap-2 flex-wrap">{REC_CATEGORIES.map(c => <Badge key={c.value} className={`${c.color} border cursor-default text-[10px]`}><c.icon className="w-3 h-3 mr-1" />{c.label}</Badge>)}</div>
            {isAdmin && <Button variant="outline" size="sm" onClick={handleExportRecs} data-testid="export-recs-btn"><Download className="w-3.5 h-3.5 mr-1" />CSV</Button>}
          </div>
          {recognitions.length === 0 ? (
            <Card><CardContent className="p-10 text-center text-slate-500" data-testid="rec-empty"><Award className="w-14 h-14 mx-auto mb-3 text-slate-300" /><p className="font-medium text-lg">Sin reconocimientos</p><p className="text-sm mt-1">Envía el primer reconocimiento a un compañero</p></CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
              {recognitions.map(rec => {
                const cat = getCatInfo(rec.category);
                return (
                  <Card key={rec.recognition_id} className="border-slate-200 hover:shadow-md transition-all" data-testid={`rec-${rec.recognition_id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${cat.color} border`}><cat.icon className="w-5 h-5" /></div>
                        <div className="flex-1">
                          <Badge className={`${cat.color} border text-[10px] mb-0.5`}>{cat.label}</Badge>
                          <p className="text-xs text-slate-500">{rec.from_name || "Anónimo"} <ArrowRight className="w-3 h-3 inline mx-0.5" /> <strong className="text-slate-700">{rec.to_name || getEmpName(rec.to_employee_id)}</strong></p>
                        </div>
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed">{rec.message}</p>
                      <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-2.5">
                        <ReactionBar reactions={rec.reactions} onReact={(r) => handleReactRec(rec.recognition_id, r)} userId={user?.user_id} />
                        <button onClick={() => { setCommentingOn(commentingOn === `rec-${rec.recognition_id}` ? null : `rec-${rec.recognition_id}`); setCommentText(""); }}
                          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 px-2 py-1 rounded-lg hover:bg-slate-100">
                          <MessageCircle className="w-3.5 h-3.5" />{(rec.comments || []).length}
                        </button>
                      </div>
                      {(rec.comments || []).length > 0 && (
                        <div className="mt-2 space-y-1.5">{rec.comments.slice(-2).map(c => (
                          <div key={c.comment_id} className="flex items-start gap-2"><div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-[8px] font-bold text-slate-600 shrink-0 mt-0.5">{c.author_name?.charAt(0)}</div><div className="bg-slate-50 rounded-lg px-2.5 py-1 flex-1"><p className="text-[10px] font-semibold text-slate-600">{c.author_name}</p><p className="text-[11px] text-slate-600">{c.text}</p></div></div>
                        ))}</div>
                      )}
                      {commentingOn === `rec-${rec.recognition_id}` && (
                        <div className="mt-2 flex gap-2">
                          <Input value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Comentar..." className="text-sm h-8" onKeyDown={e => e.key === "Enter" && handleCommentRec(rec.recognition_id)} />
                          <Button size="sm" onClick={() => handleCommentRec(rec.recognition_id)} className="h-8 bg-emerald-600 hover:bg-emerald-700"><Send className="w-3 h-3" /></Button>
                        </div>
                      )}
                      <p className="text-[10px] text-slate-400 mt-2">{rec.created_at ? new Date(rec.created_at).toLocaleDateString("es-MX", { day: "numeric", month: "short" }) : ""}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ===== CHAT GRUPAL ===== */}
        <TabsContent value="chat">
          <div className="flex gap-4 h-[65vh]">
            {/* Sidebar */}
            <div className="w-72 shrink-0 border border-slate-200 rounded-xl overflow-hidden bg-white flex flex-col">
              <div className="p-3 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-semibold text-sm text-slate-900">Grupos</h3>
                {isAdmin && <Button size="sm" variant="ghost" onClick={() => setShowGroupDlg(true)} data-testid="create-group-btn"><Plus className="w-4 h-4" /></Button>}
              </div>
              <div className="flex-1 overflow-y-auto">
                {chatGroups.length === 0 ? (
                  <div className="text-center py-8 text-slate-400 text-xs px-4" data-testid="chat-empty-sidebar">
                    <Hash className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p>Sin grupos de chat</p>
                    {isAdmin && <p className="mt-1">Crea el primer grupo</p>}
                  </div>
                ) : chatGroups.map(g => (
                  <button key={g.group_id} onClick={() => setCurrentGroup(g)}
                    className={`w-full text-left p-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${currentGroup?.group_id === g.group_id ? "bg-emerald-50 border-l-2 border-l-emerald-500" : ""}`}
                    data-testid={`chat-group-${g.group_id}`}>
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                        {g.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-900 truncate">{g.name}</p>
                        <p className="text-[10px] text-slate-400 truncate">{g.last_message || "Sin mensajes"}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            {/* Chat Area */}
            <div className="flex-1 border border-slate-200 rounded-xl overflow-hidden bg-white flex flex-col">
              {!currentGroup ? (
                <div className="flex-1 flex items-center justify-center text-slate-400" data-testid="chat-select-group">
                  <div className="text-center"><MessageSquare className="w-16 h-16 mx-auto mb-3 text-slate-300" /><p className="font-medium">Selecciona un grupo</p><p className="text-sm">o crea uno nuevo para comenzar</p></div>
                </div>
              ) : (
                <>
                  <div className="p-3 border-b border-slate-200 bg-slate-50 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold">{currentGroup.name?.charAt(0)}</div>
                    <div><p className="font-semibold text-sm text-slate-900">{currentGroup.name}</p><p className="text-[10px] text-slate-500">{currentGroup.members?.length || 0} miembros</p></div>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/50" data-testid="chat-messages-area">
                    {messages.length === 0 && <div className="text-center text-slate-400 text-sm py-10">Sin mensajes aún. Envía el primero.</div>}
                    {messages.map(msg => {
                      const isMe = msg.sender_id === user?.user_id;
                      return (
                        <div key={msg.message_id} className={`flex ${isMe ? "justify-end" : "justify-start"}`} data-testid={`msg-${msg.message_id}`}>
                          <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${isMe ? "bg-emerald-600 text-white rounded-br-md" : "bg-white border border-slate-200 text-slate-800 rounded-bl-md shadow-sm"}`}>
                            {!isMe && <p className="text-[10px] font-semibold mb-0.5 text-emerald-600">{msg.sender_name}</p>}
                            {msg.text && <p className="text-sm leading-relaxed">{msg.text}</p>}
                            {msg.file_name && (
                              <button onClick={() => handleDownloadFile(msg.message_id, msg.file_name)}
                                className={`mt-1.5 flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-lg ${isMe ? "bg-emerald-700/50 hover:bg-emerald-700/70 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-700"} transition-colors`}
                                data-testid={`download-file-${msg.message_id}`}>
                                {msg.file_data ? <><Download className="w-3.5 h-3.5" />{msg.file_name}</> : <><FileText className="w-3.5 h-3.5 opacity-50" /><span className="line-through opacity-60">{msg.file_name}</span><span className="text-[10px] opacity-60">(descargado)</span></>}
                              </button>
                            )}
                            <p className={`text-[9px] mt-1 ${isMe ? "text-emerald-200" : "text-slate-400"}`}>
                              {msg.created_at ? new Date(msg.created_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" }) : ""}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={msgEndRef} />
                  </div>
                  {/* Input */}
                  <div className="p-3 border-t border-slate-200 bg-white">
                    {chatFile && (
                      <div className="flex items-center gap-2 mb-2 px-3 py-1.5 rounded-lg bg-violet-50 border border-violet-200 text-xs text-violet-700">
                        <Paperclip className="w-3.5 h-3.5" /><span className="truncate flex-1">{chatFile.name}</span>
                        <button onClick={() => setChatFile(null)}><X className="w-3.5 h-3.5 text-violet-400 hover:text-violet-700" /></button>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <label className="p-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors" data-testid="attach-file-btn">
                        <Paperclip className="w-4 h-4 text-slate-500" />
                        <input type="file" className="hidden" onChange={e => setChatFile(e.target.files[0])} />
                      </label>
                      <Input value={chatInput} onChange={e => setChatInput(e.target.value)} placeholder="Escribe un mensaje..."
                        className="flex-1" onKeyDown={e => e.key === "Enter" && handleSendMessage()} data-testid="chat-input" />
                      <Button onClick={handleSendMessage} className="bg-emerald-600 hover:bg-emerald-700" data-testid="send-msg-btn"><Send className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ===== BIRTHDAYS ===== */}
        <TabsContent value="birthdays">
          {birthdays.length === 0 ? (
            <Card><CardContent className="p-10 text-center text-slate-500" data-testid="birthdays-empty"><Cake className="w-14 h-14 mx-auto mb-3 text-slate-300" /><p className="font-medium">No hay cumpleaños próximos</p></CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
              {birthdays.map((b, i) => (
                <Card key={i} className={`border-slate-200 hover:shadow-md transition-all ${b.days_until === 0 ? "border-l-4 border-l-pink-500 bg-pink-50/30" : ""}`} data-testid={`birthday-${i}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${b.days_until === 0 ? "bg-pink-100" : "bg-violet-100"}`}>
                        <Cake className={`w-6 h-6 ${b.days_until === 0 ? "text-pink-600" : "text-violet-600"}`} />
                      </div>
                      <div>
                        <p className="font-bold text-slate-900">{b.name}</p>
                        <p className="text-sm text-slate-500">{b.department}</p>
                        <div className="flex items-center gap-1.5 mt-1">
                          <Calendar className="w-3 h-3 text-slate-400" />
                          <span className="text-xs text-slate-500">{new Date(b.birth_date).toLocaleDateString("es-MX", { day: "numeric", month: "long" })}</span>
                        </div>
                      </div>
                    </div>
                    <Badge className={`mt-3 ${b.days_until === 0 ? "bg-pink-100 text-pink-700" : b.days_until <= 7 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                      {b.days_until === 0 ? "Hoy!" : b.days_until === 1 ? "Mañana" : `En ${b.days_until} días`}
                    </Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="notifications">
          <div className="grid xl:grid-cols-[1.05fr_0.95fr] gap-6">
            <div className="space-y-6">
              <Card className="border-slate-200">
                <CardHeader><CardTitle className="text-base">Gupshup por tenant</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>WABA / nombre</Label><Input value={gupshupConfig.waba_name || ""} onChange={e => setGupshupConfig(p => ({ ...p, waba_name: e.target.value }))} data-testid="gupshup-waba-input" /></div>
                    <div><Label>App name</Label><Input value={gupshupConfig.app_name || ""} onChange={e => setGupshupConfig(p => ({ ...p, app_name: e.target.value }))} data-testid="gupshup-app-name-input" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Teléfono WABA</Label><Input value={gupshupConfig.phone_number || ""} onChange={e => setGupshupConfig(p => ({ ...p, phone_number: e.target.value }))} data-testid="gupshup-phone-input" /></div>
                    <div><Label>API Key</Label><Input type="password" value={gupshupConfig.credentials?.api_key || ""} onChange={e => setGupshupConfig(p => ({ ...p, credentials: { ...p.credentials, api_key: e.target.value } }))} data-testid="gupshup-api-key-input" /></div>
                  </div>
                  <div className="flex gap-2"><Button onClick={handleSaveGupshup} data-testid="save-gupshup-config-btn">Guardar Gupshup</Button><Button variant="outline" onClick={handleTestGupshup} data-testid="test-gupshup-config-btn">Probar conexión</Button></div>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardHeader><CardTitle className="text-base">Difusión masiva multicanal</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div><Label>Título</Label><Input value={broadcastForm.title} onChange={e => setBroadcastForm(p => ({ ...p, title: e.target.value }))} data-testid="broadcast-title-input" /></div>
                  <div><Label>Mensaje</Label><Textarea rows={3} value={broadcastForm.message} onChange={e => setBroadcastForm(p => ({ ...p, message: e.target.value }))} data-testid="broadcast-message-input" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Centro</Label><Select value={broadcastForm.audience.work_center_id || "all"} onValueChange={value => setBroadcastForm(p => ({ ...p, audience: { ...p.audience, work_center_id: value === 'all' ? '' : value } }))}><SelectTrigger data-testid="broadcast-work-center-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Toda la empresa</SelectItem>{workCenters.map(center => <SelectItem key={center.id} value={center.id}>{center.name}</SelectItem>)}</SelectContent></Select></div>
                    <div><Label>Departamento</Label><Select value={broadcastForm.audience.department || "all"} onValueChange={value => setBroadcastForm(p => ({ ...p, audience: { ...p.audience, department: value === 'all' ? '' : value } }))}><SelectTrigger data-testid="broadcast-department-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Todos</SelectItem>{[...new Set(employees.map(employee => employee.department).filter(Boolean))].map(department => <SelectItem key={department} value={department}>{department}</SelectItem>)}</SelectContent></Select></div>
                  </div>
                  <div className="flex gap-4 text-sm" data-testid="broadcast-channels-list">
                    {['email','whatsapp','internal_chat'].map(channel => <label key={channel} className="flex items-center gap-2"><input type="checkbox" checked={broadcastForm.channels.includes(channel)} onChange={() => setBroadcastForm(p => ({ ...p, channels: p.channels.includes(channel) ? p.channels.filter(item => item !== channel) : [...p.channels, channel] }))} data-testid={`broadcast-channel-${channel}`} />{channel}</label>)}
                  </div>
                  <Button onClick={handleBroadcast} className="bg-emerald-600 hover:bg-emerald-700" data-testid="send-broadcast-btn">Registrar difusión</Button>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="border-slate-200">
                <CardHeader><CardTitle className="text-base">Reglas de notificación</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div><Label>Nombre</Label><Input value={ruleForm.name} onChange={e => setRuleForm(p => ({ ...p, name: e.target.value }))} data-testid="notification-rule-name-input" /></div>
                  <div><Label>Evento</Label><Select value={ruleForm.event_type} onValueChange={value => setRuleForm(p => ({ ...p, event_type: value }))}><SelectTrigger data-testid="notification-rule-event-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="leave_request_created">Solicitud de permiso creada</SelectItem><SelectItem value="payroll_batch_created">Lote de nómina creado</SelectItem><SelectItem value="vacancy_request_created">Solicitud de vacante creada</SelectItem><SelectItem value="review_task_assigned">Shortlist asignada</SelectItem></SelectContent></Select></div>
                  <div><Label>Plantilla</Label><Textarea rows={2} value={ruleForm.message_template} onChange={e => setRuleForm(p => ({ ...p, message_template: e.target.value }))} data-testid="notification-rule-template-input" /></div>
                  <div className="flex gap-4 text-sm">{['email','whatsapp','internal_chat'].map(channel => <label key={channel} className="flex items-center gap-2"><input type="checkbox" checked={ruleForm.channels.includes(channel)} onChange={() => setRuleForm(p => ({ ...p, channels: p.channels.includes(channel) ? p.channels.filter(item => item !== channel) : [...p.channels, channel] }))} data-testid={`notification-rule-channel-${channel}`} />{channel}</label>)}</div>
                  <Button onClick={handleSaveRule} data-testid="save-notification-rule-btn">Guardar regla</Button>
                  <div className="space-y-2">{rules.map(rule => <div key={rule.rule_id} className="p-3 rounded-xl border border-slate-200"><p className="font-medium text-slate-900">{rule.name}</p><p className="text-xs text-slate-500">{rule.event_type} · {rule.channels.join(', ')}</p></div>)}</div>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardHeader><CardTitle className="text-base">Bitácora</CardTitle></CardHeader>
                <CardContent className="space-y-2 max-h-[360px] overflow-y-auto" data-testid="notification-logs-list">
                  {notificationLogs.slice(0, 20).map(log => <div key={log.log_id} className="p-3 rounded-xl bg-slate-50 border border-slate-200"><div className="flex items-center justify-between gap-3"><p className="font-medium text-slate-900 text-sm">{log.channel} · {log.status}</p><span className="text-[10px] text-slate-400">{new Date(log.created_at).toLocaleString('es-MX')}</span></div><p className="text-xs text-slate-500 mt-1">{log.recipient_name} · {log.event_type}</p></div>)}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* === POST DIALOG === */}
      <Dialog open={showPostDlg} onOpenChange={setShowPostDlg}>
        <DialogContent><DialogHeader><DialogTitle>Nueva Publicación</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Título *</Label><Input value={newPost.title} onChange={e => setNewPost({ ...newPost, title: e.target.value })} data-testid="post-title" /></div>
            <div><Label>Contenido</Label><Textarea rows={4} value={newPost.content} onChange={e => setNewPost({ ...newPost, content: e.target.value })} data-testid="post-content" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Prioridad</Label>
                <Select value={newPost.priority} onValueChange={v => setNewPost({ ...newPost, priority: v })}>
                  <SelectTrigger data-testid="post-priority"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="normal">Normal</SelectItem><SelectItem value="high">Importante</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={newPost.pinned} onChange={e => setNewPost({ ...newPost, pinned: e.target.checked })} className="w-4 h-4 rounded border-slate-300" />
                  <span className="text-sm text-slate-700">Fijar al inicio</span>
                </label>
              </div>
            </div>
            <Button onClick={handleCreatePost} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-post-btn">Publicar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* === RECOGNITION DIALOG === */}
      <Dialog open={showRecDlg} onOpenChange={setShowRecDlg}>
        <DialogContent><DialogHeader><DialogTitle>Enviar Reconocimiento</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Colaborador *</Label>
              <Select value={newRec.to_employee_id} onValueChange={v => setNewRec({ ...newRec, to_employee_id: v })}>
                <SelectTrigger data-testid="rec-employee"><SelectValue placeholder="Seleccionar empleado" /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Categoría</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {REC_CATEGORIES.map(c => (
                  <button key={c.value} onClick={() => setNewRec({ ...newRec, category: c.value })}
                    className={`p-2.5 rounded-lg border-2 text-center transition-all ${newRec.category === c.value ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}
                    data-testid={`rec-cat-${c.value}`}>
                    <c.icon className={`w-5 h-5 mx-auto mb-1 ${newRec.category === c.value ? "text-emerald-600" : "text-slate-400"}`} />
                    <p className="text-[10px] font-medium">{c.label}</p>
                  </button>
                ))}
              </div>
            </div>
            <div><Label>Mensaje *</Label><Textarea rows={3} value={newRec.message} onChange={e => setNewRec({ ...newRec, message: e.target.value })} placeholder="Por qué reconoces a esta persona..." data-testid="rec-message" /></div>
            <Button onClick={handleCreateRec} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-rec-btn"><Award className="w-4 h-4 mr-2" />Enviar Reconocimiento</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* === CREATE GROUP DIALOG === */}
      <Dialog open={showGroupDlg} onOpenChange={setShowGroupDlg}>
        <DialogContent><DialogHeader><DialogTitle>Nuevo Grupo de Chat</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nombre del Grupo *</Label><Input value={newGroup.name} onChange={e => setNewGroup({ ...newGroup, name: e.target.value })} placeholder="Equipo Marketing" data-testid="group-name" /></div>
            <div><Label>Descripción</Label><Input value={newGroup.description} onChange={e => setNewGroup({ ...newGroup, description: e.target.value })} placeholder="Opcional" data-testid="group-desc" /></div>
            <div><Label>Miembros</Label>
              <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg mt-1">
                {chatUsers.map(u => (
                  <label key={u.user_id} className="flex items-center gap-2.5 p-2.5 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0" data-testid={`member-${u.user_id}`}>
                    <input type="checkbox" checked={newGroup.members.includes(u.user_id)} onChange={e => {
                      if (e.target.checked) setNewGroup({ ...newGroup, members: [...newGroup.members, u.user_id] });
                      else setNewGroup({ ...newGroup, members: newGroup.members.filter(m => m !== u.user_id) });
                    }} className="w-4 h-4 rounded border-slate-300" />
                    <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600">{u.name?.charAt(0) || u.email?.charAt(0)}</div>
                    <div><p className="text-sm font-medium text-slate-900">{u.name || u.email}</p><p className="text-[10px] text-slate-400">{u.role}</p></div>
                  </label>
                ))}
              </div>
              {newGroup.members.length > 0 && <p className="text-xs text-slate-500 mt-1">{newGroup.members.length} seleccionados</p>}
            </div>
            <Button onClick={handleCreateGroup} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-group-btn"><UserPlus className="w-4 h-4 mr-2" />Crear Grupo</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Communications;
